import { useState, useEffect, useCallback } from 'react';
import { useIonRouter } from '@ionic/react';
import { DataTable } from '../shared/DataTable';
import { AddAgentModal } from '../shared/AddAgentModal';
import { searchAgents } from '../../services/agentService';

import { useRefresh } from '../../contexts/RefreshContext';
import type { Agent } from '../../types/agent';
import type { Column } from '../shared/DataTable';

const columns: Column<Agent>[] = [
  { key: 'name', label: 'Name' },
  { key: 'user', label: 'User', render: (item) => item.profileSummary?.displayName || '—' },
];

interface AgentListProps {
  title?: string;
  clientId?: string;
  propertyId?: string;
}

export function AgentList({ title = 'Agents', clientId, propertyId }: AgentListProps) {
  const router = useIonRouter();

  const [data, setData] = useState<Agent[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const filter = clientId ? { clientId } : {};
      const results = await searchAgents(filter, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  const refreshPath = `/agents-${clientId || propertyId || 'all'}`;
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler(refreshPath, fetch);
    return () => unregisterRefreshHandler(refreshPath);
  }, [fetch, refreshPath, registerRefreshHandler, unregisterRefreshHandler]);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <DataTable
        title={title}
        columns={columns}
        data={data}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        onRefresh={fetch}
        onAdd={() => setShowAddModal(true)}
        actions={(item) => (
          <button
            onClick={() => router.push(`/calls?agentId=${item.id}${clientId ? `&clientId=${clientId}` : ''}${propertyId ? `&propertyId=${propertyId}` : ''}`, 'none')}
            className="text-xs px-2 py-1 rounded text-white transition-colors" style={{ backgroundColor: 'var(--ion-color-secondary)' }}
          >
            Calls
          </button>
        )}
      />
      {showAddModal && (
        <AddAgentModal
          clientId={clientId}
          propertyId={propertyId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetch(); }}
        />
      )}
    </div>
  );
}
